import React, { useRef, useState } from 'react'
import { Popover } from 'antd'
import { ColumnType } from 'antd/lib/table'
import { CatalogItemResponse, CatalogItemType, EventCatalogItemResponse, ProductCatalogItemResponse } from './types'
import { formatEventLocation } from './helpers'
import { TableRowEntityDisplay } from '../../components/table-row-entity-display/table-row-entity-display'

const ellipsisStyle: React.CSSProperties = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    cursor: 'default',
}

/**
 * Renders text with ellipsis truncation and popover on hover only when text is truncated.
 */
function OverflowPopover({ text, children }: { text: string; children?: React.ReactNode }): React.ReactElement {
    const ref = useRef<HTMLSpanElement>(null)
    const [isTruncated, setIsTruncated] = useState(false)

    const handleMouseEnter = () => {
        if (ref.current) {
            setIsTruncated(ref.current.scrollWidth > ref.current.clientWidth)
        }
    }

    const content = (
        <span ref={ref} style={ellipsisStyle} onMouseEnter={handleMouseEnter}>
            {children ?? text}
        </span>
    )

    if (!isTruncated) {
        return content
    }

    return <Popover content={<div style={{ maxWidth: 400, wordBreak: 'break-word' }}>{text}</div>}>{content}</Popover>
}

function renderTextWithPopover(text: string | undefined | null): React.ReactNode {
    if (!text) {
        return null
    }

    return <OverflowPopover text={text} />
}

/**
 * Returns column definitions for the catalog table based on item type.
 */
export function getColumnsForItemType(itemType: CatalogItemType): ColumnType<CatalogItemResponse>[] {
    const columns: ColumnType<CatalogItemResponse>[] = [
        {
            key: 'image',
            dataIndex: 'image_url',
            title: 'Image',
            width: 80,
            render: (imageUrl: string | undefined | null) => {
                if (!imageUrl) {
                    return (
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                backgroundColor: '#f0f0f0',
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <span style={{ color: '#999', fontSize: 10 }}>No image</span>
                        </div>
                    )
                }

                return (
                    <React.Fragment>
                        <img
                            src={imageUrl}
                            alt="Item thumbnail"
                            style={{
                                width: 48,
                                height: 48,
                                objectFit: 'cover',
                                borderRadius: 4,
                            }}
                            onError={(e) => {
                                const target = e.currentTarget
                                target.style.display = 'none'
                                const placeholder = target.nextElementSibling as HTMLElement
                                if (placeholder) {
                                    placeholder.style.display = 'flex'
                                }
                            }}
                        />
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                backgroundColor: '#f0f0f0',
                                borderRadius: 4,
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <span style={{ color: '#999', fontSize: 10 }}>No image</span>
                        </div>
                    </React.Fragment>
                )
            },
        },
        {
            key: 'name',
            dataIndex: 'name',
            title: 'Name',
            ellipsis: { showTitle: false },
            render: (name: string, record: CatalogItemResponse) => {
                const titleContent = record.url ? (
                    <a href={record.url} target="_blank" rel="noopener noreferrer">
                        {name}
                    </a>
                ) : (
                    name
                )

                return (
                    <TableRowEntityDisplay
                        title={titleContent}
                        description={record.description ? <OverflowPopover text={record.description} /> : undefined}
                    />
                )
            },
        },
    ]

    if (itemType === CatalogItemType.PRODUCT) {
        columns.push({
            key: 'brand_name',
            dataIndex: 'brand_name',
            title: 'Brand Name',
            width: 150,
            ellipsis: { showTitle: false },
            render: (brandName: string | undefined | null) => {
                return renderTextWithPopover(brandName)
            },
        })

        columns.push({
            key: 'category',
            dataIndex: 'category_path',
            title: 'Category',
            width: 200,
            ellipsis: { showTitle: false },
            render: (_: any, record: CatalogItemResponse) => {
                const productRecord = record as ProductCatalogItemResponse
                const path = productRecord.category_path
                if (!path || path.length === 0) {
                    return null
                }
                const text = path.join(' > ')
                return renderTextWithPopover(text)
            },
        })
    }

    if (itemType === CatalogItemType.TICKETED_EVENT) {
        columns.push({
            key: 'genre',
            dataIndex: 'genre_major',
            title: 'Genre',
            width: 180,
            ellipsis: { showTitle: false },
            render: (_: any, record: CatalogItemResponse) => {
                const eventRecord = record as EventCatalogItemResponse
                const parts = [eventRecord.genre_major, eventRecord.genre_minor].filter(
                    (p) => p !== undefined && p !== null && p !== '',
                )
                return renderTextWithPopover(parts.length > 0 ? parts.join(' / ') : null)
            },
        })

        columns.push({
            key: 'performer',
            dataIndex: 'performer_name',
            title: 'Performer',
            width: 180,
            ellipsis: { showTitle: false },
            render: (performerName: string | undefined | null) => {
                return renderTextWithPopover(performerName)
            },
        })

        columns.push({
            key: 'location',
            dataIndex: 'location_city',
            title: 'Location',
            ellipsis: { showTitle: false },
            render: (_: any, record: CatalogItemResponse) => {
                const eventRecord = record as EventCatalogItemResponse
                const location = formatEventLocation(
                    eventRecord.location_city ?? undefined,
                    eventRecord.location_region ?? undefined,
                    eventRecord.location_country ?? undefined,
                )
                return renderTextWithPopover(location || null)
            },
        })

        columns.push({
            key: 'availability',
            dataIndex: 'availability_primary',
            title: 'Availability',
            width: 150,
            ellipsis: { showTitle: false },
            render: (availability: string | undefined | null) => {
                return renderTextWithPopover(availability)
            },
        })
    }

    if (itemType === CatalogItemType.RECIPE) {
        columns.push({
            key: 'category',
            dataIndex: 'recipe_category',
            title: 'Category',
            width: 150,
            ellipsis: { showTitle: false },
            render: (category: string | undefined | null) => {
                return renderTextWithPopover(category)
            },
        })

        columns.push({
            key: 'cuisine',
            dataIndex: 'recipe_cuisine',
            title: 'Cuisine',
            width: 150,
            ellipsis: { showTitle: false },
            render: (cuisine: string | undefined | null) => {
                return renderTextWithPopover(cuisine)
            },
        })

        columns.push({
            key: 'keywords',
            dataIndex: 'keywords',
            title: 'Keywords',
            width: 200,
            ellipsis: { showTitle: false },
            render: (keywords: string[] | undefined | null) => {
                if (!keywords || keywords.length === 0) {
                    return null
                }
                const text = keywords.join(', ')
                return renderTextWithPopover(text)
            },
        })
    }

    return columns
}
